import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Hotel, Building2, Gem, ShoppingBag } from 'lucide-react';
import { useScrollReveal } from '../hooks/useAnimations';
import { useQuote } from '../context/QuoteContext';

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

interface Product {
  name: string;
  volume: string;
  tagline: string;
  features: string[];
  image: string;
}

const products: Product[] = [
  {
    name: 'Arohix 200ml',
    volume: '200 ml',
    tagline: 'Compact hydration for events, conferences & on-the-go',
    features: ['BIS Certified', 'Ozone-Sealed', 'Event-Ready Pack'],
    image: '/assets/aerohix-water-200ml.png',
  },
  {
    name: 'Arohix 500ml',
    volume: '500 ml',
    tagline: 'Your everyday companion with active minerals',
    features: ['Ergonomic Design', 'On-the-Go', '7-Stage Pure'],
    image: '/assets/aerohix-water-500ml.png',
  },
  {
    name: 'Arohix 1L',
    volume: '1 Litre',
    tagline: 'Family-size purity for home & office',
    features: ['Family Pack', 'Mineral Enriched', 'Dual Ozonated'],
    image: '/assets/aerohix-water-1l.jpeg',
  },
];

const Products: React.FC = () => {
  const { openQuote } = useQuote();
  return (
    <main className="bg-white overflow-hidden">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-14 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-silver-50 via-brand-50/30 to-white" />
        <div className="absolute inset-0 dot-grid opacity-[0.3]" />
        <div className="absolute top-20 right-[20%] w-[450px] h-[450px] bg-brand-200/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5 animate-fade-in">Product Range</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-silver-950 tracking-tight leading-[1.1] animate-slide-up">
            Water for Every{' '}
            <span className="text-gradient">Occasion</span>
          </h1>
          <p className="mt-7 text-lg text-silver-500 max-w-2xl mx-auto leading-relaxed animate-slide-up-delayed">
            Arohix delivers 7-stage purified, mineral-enriched packaged drinking water in 200ml, 500ml, and 1 Litre formats.
          </p>
        </div>
      </section>

      {/* ═══ PRODUCT GRID ═══ */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, i) => (
              <Reveal key={product.name} delay={i * 80}>
                <div className="group bg-white border border-silver-100 rounded-2xl overflow-hidden hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border h-full flex flex-col">
                  {/* Bottle Visual */}
                  <div className="relative bg-gradient-to-br from-sky-50/60 to-brand-50/40 overflow-hidden aspect-[4/3]">
                    <div className="absolute top-3 right-3 z-10">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/80 backdrop-blur-sm text-silver-600">
                        {product.volume}
                      </span>
                    </div>
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-7 flex-1 flex flex-col">
                    <h3 className="text-lg font-bold text-silver-900">{product.name}</h3>
                    <p className="text-sm text-silver-500 mt-1.5 leading-relaxed">{product.tagline}</p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {product.features.map(f => (
                        <span key={f} className="inline-flex items-center gap-1 text-[11px] font-medium text-silver-600 bg-silver-50 px-2.5 py-1 rounded-full">
                          <ShieldCheck className="w-3 h-3 text-emerald-500" />
                          {f}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto pt-6">
                      <button onClick={openQuote} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors duration-200 group/link">
                        Request Quote
                        <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform duration-200" />
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CUSTOM BRANDING ═══ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-silver-50/50 to-white relative">
        <div className="absolute inset-0 dot-grid opacity-[0.2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5">Custom Solutions</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                  Your Brand. <span className="text-gradient">Our Purity.</span>
                </h2>
                <p className="mt-6 text-silver-600 leading-relaxed text-[15px]">
                  Elevate your brand with custom-labeled Arohix bottles. Perfect for hotels, corporate events, weddings, restaurants, and premium retail outlets.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    'Custom label design & printing',
                    'Flexible MOQ for events & businesses',
                    'Premium shrink-wrap finishing',
                    'Dedicated account management',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-silver-600">
                      <div className="w-5 h-5 rounded-full bg-brand-50 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <ShieldCheck className="w-3.5 h-3.5 text-brand-600" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <button onClick={openQuote} className="shimmer-btn group inline-flex items-center gap-2.5 bg-brand-600 text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-700 hover:shadow-xl hover:shadow-brand-200/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                    Discuss Custom Branding
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Hotels & Resorts', icon: <Hotel className="w-7 h-7 text-brand-500" />, desc: 'Room service & lobby branded bottles' },
                  { label: 'Corporate Events', icon: <Building2 className="w-7 h-7 text-violet-500" />, desc: 'Conference & meeting room supplies' },
                  { label: 'Weddings', icon: <Gem className="w-7 h-7 text-amber-500" />, desc: 'Custom ceremony & reception bottles' },
                  { label: 'Premium Retail', icon: <ShoppingBag className="w-7 h-7 text-emerald-500" />, desc: 'White-label shelf-ready packaging' },
                ].map((item, i) => (
                  <div key={i} className="group bg-white border border-silver-100 rounded-2xl p-6 hover:shadow-card hover:-translate-y-0.5 transition-all duration-300 text-center gradient-border">
                    <div className="flex justify-center mb-3">{item.icon}</div>
                    <h4 className="text-sm font-bold text-silver-900">{item.label}</h4>
                    <p className="text-xs text-silver-500 mt-1.5 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.03] rounded-full" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Ready to Order?
            </h2>
            <p className="mt-6 text-lg text-brand-100/70 max-w-xl mx-auto leading-relaxed">
              Contact our team for bulk pricing, custom branding options, and distribution partnerships.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button onClick={openQuote} className="shimmer-btn group inline-flex items-center gap-2.5 bg-white text-brand-700 px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-50 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                Get a Quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <Link to="/science" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300">
                Our Process <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
};

export default Products;
